import {Nullable} from "../../models/common/Nullable";
import {
    errorFetchingRequestState,
    getInitialRequestState,
    RequestState,
    startFetchingRequestState,
    successFetchingRequestState
} from "../../models/common/RequestState";
import {AllTimesheetListTypes, AllTimesheetTypes} from "../../models/timesheet/Timesheet";
import {
    CREATE_TIMESHEET_ENTRY,
    CREATE_TIMESHEET_ENTRY_ERROR,
    CREATE_TIMESHEET_ENTRY_SUCCESS,
    CreateTimesheetEntryActionTypes,
    DELETE_TIMESHEET_ENTRY,
    DELETE_TIMESHEET_ENTRY_ERROR,
    DELETE_TIMESHEET_ENTRY_SUCCESS,
    DeleteTimesheetEntryActionTypes,
    FETCH_SERIES_APPOINTMENT,
    FETCH_SERIES_APPOINTMENT_ERROR,
    FETCH_SERIES_APPOINTMENT_SUCCESS,
    FETCH_TIMESHEET_ENTRIES,
    FETCH_TIMESHEET_ENTRIES_ERROR,
    FETCH_TIMESHEET_ENTRIES_SUCCESS,
    FETCH_TIMESHEET_ENTRY,
    FETCH_TIMESHEET_ENTRY_ERROR,
    FETCH_TIMESHEET_ENTRY_SUCCESS,
    FetchSeriesAppointmentsActionTypes,
    FetchTimesheetEntryActionTypes,
    IMPORT_TIMESHEET_ENTRY, IMPORT_TIMESHEET_ENTRY_ERROR,
    IMPORT_TIMESHEET_ENTRY_SUCCESS,
    RESTORE_TIMESHEET_ENTRY,
    RESTORE_TIMESHEET_ENTRY_ERROR,
    RESTORE_TIMESHEET_ENTRY_SUCCESS,
    RestoreTimesheetEntryActionTypes,
    TimesheetViewActionTypes,
    UPDATE_TIMESHEET_ENTRY,
    UPDATE_TIMESHEET_ENTRY_ERROR,
    UPDATE_TIMESHEET_ENTRY_SUCCESS,
    UpdateTimesheetEntryActionTypes,
} from "../../actions/timesheet/TimesheetActions";
import {UserViewSettings} from "../../models/common/ViewSettings";


export interface TimesheetState {
    totalEntries: number
    entries: {
        current: Nullable<AllTimesheetListTypes>
        request: RequestState
    }
    detailedEntries: {
        [ key: number ]: AllTimesheetTypes
        fetchRequest: RequestState
        createRequest: RequestState
        deleteRequest: RequestState
        updateRequest: RequestState
        restoreRequest: RequestState
    }
    seriesEntries: {
        [key: number]: {
            totalEntries: number
            entries: Nullable< AllTimesheetListTypes >
            userViewSettings: UserViewSettings
        }
        request: RequestState
    }
}

export const initialState: TimesheetState = {
    totalEntries: 0,
    entries: {
        current: null,
        request: getInitialRequestState()
    },
    detailedEntries: {
        fetchRequest: getInitialRequestState(),
        createRequest: getInitialRequestState(),
        deleteRequest: getInitialRequestState(),
        updateRequest: getInitialRequestState(),
        restoreRequest: getInitialRequestState()
    },
    seriesEntries: {
        request: getInitialRequestState()
    }
};

type TimesheetReducerActionTypes = CreateTimesheetEntryActionTypes
    | TimesheetViewActionTypes
    | FetchTimesheetEntryActionTypes
    | FetchSeriesAppointmentsActionTypes
    | UpdateTimesheetEntryActionTypes
    | DeleteTimesheetEntryActionTypes
    | RestoreTimesheetEntryActionTypes

export const TimesheetReducer = ( state = initialState, action: TimesheetReducerActionTypes  ) => {
    switch( action.type ) {

        case FETCH_TIMESHEET_ENTRIES:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case FETCH_TIMESHEET_ENTRIES_SUCCESS:
            return {
                ...state,
                totalEntries: action.payload.totalEntries,
                entries: {
                    current: action.payload.entries,
                    request: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                        error: null
                    }
                }
            };
        case FETCH_TIMESHEET_ENTRIES_ERROR:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };

        case RESTORE_TIMESHEET_ENTRY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    restoreRequest: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };

        case RESTORE_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    restoreRequest: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };

        case RESTORE_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    restoreRequest: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };
        case DELETE_TIMESHEET_ENTRY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    deleteRequest: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case DELETE_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    deleteRequest: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };
        case DELETE_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    deleteRequest: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };
        case UPDATE_TIMESHEET_ENTRY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    updateRequest: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case UPDATE_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.entry.timesheetEntryId.rawValue ]: action.entry,
                    updateRequest: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };
        case UPDATE_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    updateRequest: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };
        case CREATE_TIMESHEET_ENTRY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    createRequest: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case CREATE_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.entry.timesheetEntryId.rawValue ]: action.entry,
                    createRequest: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };
        case CREATE_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    createRequest: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };

        case FETCH_TIMESHEET_ENTRY:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    fetchRequest: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case FETCH_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    [ action.entry.timesheetEntryId.rawValue ]: action.entry,
                    fetchRequest: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };
        case FETCH_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                detailedEntries: {
                    ...state.detailedEntries,
                    fetchRequest: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };
        case FETCH_SERIES_APPOINTMENT:
            return {
                ...state,
                seriesEntries: {
                    ...state.seriesEntries,
                    request: {
                        isFetching: true,
                        lastRequestSuccess: 0,
                        error: null
                    }
                }
            };
        case FETCH_SERIES_APPOINTMENT_SUCCESS:
            return {
                ...state,
                seriesEntries: {
                    ...state.seriesEntries,
                    [action.seriesId.rawValue]: {
                        ...state.seriesEntries[ action.seriesId.rawValue ],
                        totalEntries: action.payload.totalEntries,
                        entries: action.payload.entries,
                    },
                    request: {
                        isFetching: false,
                        lastRequestSuccess: Date.now(),
                    }
                },
            };
        case FETCH_SERIES_APPOINTMENT_ERROR:
            return {
                ...state,
                seriesEntries: {
                    ...state.seriesEntries,
                    request: {
                        isFetching: false,
                        error: action.error
                    }
                }
            };
        case IMPORT_TIMESHEET_ENTRY:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: startFetchingRequestState()
                }
            };
        case IMPORT_TIMESHEET_ENTRY_SUCCESS:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: successFetchingRequestState()
                }
            };
        case IMPORT_TIMESHEET_ENTRY_ERROR:
            return {
                ...state,
                entries: {
                    ...state.entries,
                    request: errorFetchingRequestState( action.error )
                }
            };
        default:
            return state;
    }
};